import { ChangeEvent, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';

import {
  NhsEligibility,
  PcseVoucherSupplement,
  PcseVoucherType,
  SubmitClaimPayload,
} from '../../@types/pcse';
import { useSubmitClaim } from '../../hooks/usePCSEClaims';
import {
  NHS_ELIGIBILITY_FLAGS,
  useNhsVoucherRates,
  useVoucherQuote,
  VOUCHER_SUPPLEMENTS,
} from './nhsVoucherRates';
import { describeEye, ReadPrescriptionResult } from './readPrescription';
import SignaturePad from './SignaturePad';
import { toUpperText, withUppercase } from '../../utils/uppercase';

const SUPPLEMENT_KEYS = Object.keys(
  VOUCHER_SUPPLEMENTS,
) as PcseVoucherSupplement[];

type EligibilityFlagKey = (typeof NHS_ELIGIBILITY_FLAGS)[number]['key'];

interface VoucherClaimFormProps {
  patientId: string;
  /** Pre-fill patient demographics (from the patient record). */
  initialPatient?: Partial<SubmitClaimPayload['patient']>;
  /** Preferred eligibility source — link to the patient's PatientNhs record. */
  patientNhsId?: number;
  /** Pre-fill eligibility flags from the patient's stored NhsEligibility. */
  initialEligibility?: Partial<NhsEligibility>;
  /**
   * The patient's current refraction. Drives the voucher band, and is sent on
   * the claim — without it the claim goes to PCSE with plano powers.
   */
  initialPrescription?: ReadPrescriptionResult;
  /** Date of the sight test the prescription came from. */
  initialTestDate?: string;
  odsCode?: string;
  onSubmitted?: (claimId: string) => void;
}

interface FormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  niNumber: string;
  nhsNumber: string;
  addressLine1: string;
  town: string;
  postcode: string;
  /** Performing optometrist's GOC registration number — mandatory on an eGOS claim. */
  prescriberGocNumber: string;
  voucherType: PcseVoucherType | '';
  supplements: PcseVoucherSupplement[];
  chargedAmount: string;
  eligibility: Record<EligibilityFlagKey, boolean>;
  isEvidenceSeen: boolean;
  testDate: string;
  patientDeclared: boolean;
  contractorSignedOff: boolean;
  patientSignature: string;
  patientSignatoryName: string;
  optometristSignature: string;
  optometristName: string;
  /** Part3-Supplier-Declaration requires its own signature. */
  supplierSignature: string;
  supplierName: string;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  dateOfBirth: Yup.string().required('Date of birth is required'),
  voucherType: Yup.string().required('Select a voucher type'),
  eligibility: Yup.object().test(
    'at-least-one',
    'Select at least one NHS eligibility reason',
    (value: Record<string, boolean> = {}) => Object.values(value).some(Boolean),
  ),
  chargedAmount: Yup.number()
    .typeError('Enter a valid amount')
    .min(0, 'Cannot be negative')
    .required('Enter the amount charged'),
  niNumber: Yup.string().when('eligibility', {
    // NI number is required when eligibility is claimed on a benefits basis.
    // TODO(business-rule): confirm against current GOS3 eligibility guidance.
    is: (v: Record<string, boolean>) => Boolean(v?.isBenefits),
    then: (s) =>
      s
        .required('NI number is required when claiming on a benefits basis')
        .matches(
          /^[A-Za-z]{2}\d{6}[A-Za-z]$/,
          'Enter a valid NI number (e.g. QQ123456C)',
        ),
    otherwise: (s) => s.notRequired(),
  }),
  patientDeclared: Yup.boolean().oneOf(
    [true],
    'The patient declaration must be confirmed',
  ),
  contractorSignedOff: Yup.boolean().oneOf(
    [true],
    'Contractor sign-off is required',
  ),
  patientSignature: Yup.string().required(
    'Patient/guardian signature is required',
  ),
  patientSignatoryName: Yup.string().required(
    'Enter the name of the person signing',
  ),
  optometristSignature: Yup.string().required(
    'Optometrist signature is required',
  ),
  optometristName: Yup.string().required('Enter the optometrist name'),
  supplierSignature: Yup.string().required('Supplier signature is required'),
  supplierName: Yup.string().required('Enter the supplier / dispenser name'),
  // The following are mandatory in the eGOS schema — PCSE rejects the claim
  // outright without them, so validate here rather than round-tripping.
  addressLine1: Yup.string().required('Patient address is required by PCSE'),
  postcode: Yup.string()
    .required('Postcode is required by PCSE')
    // PostcodeType is [0-9a-zA-Z]{1,7} once spaces are stripped, or "N/A".
    .test(
      'egos-postcode',
      'Enter a valid postcode (max 7 characters, or N/A if unknown)',
      (v) =>
        v === 'N/A' || /^[0-9a-zA-Z]{1,7}$/.test((v ?? '').replace(/\s/g, '')),
    ),
  prescriberGocNumber: Yup.string().required(
    'The optometrist GOC number is required by PCSE',
  ),
});

export default function VoucherClaimForm({
  patientId,
  initialPatient,
  patientNhsId,
  initialEligibility,
  initialPrescription,
  initialTestDate,
  odsCode,
  onSubmitted,
}: VoucherClaimFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { submit, loading, error, fieldErrors } = useSubmitClaim();

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: toUpperText(initialPatient?.firstName ?? ''),
      lastName: toUpperText(initialPatient?.lastName ?? ''),
      dateOfBirth: initialPatient?.dateOfBirth ?? '',
      niNumber: toUpperText(initialPatient?.niNumber ?? ''),
      nhsNumber: initialPatient?.nhsNumber ?? '',
      addressLine1: toUpperText(initialPatient?.addressLine1 ?? ''),
      town: toUpperText(initialPatient?.town ?? ''),
      postcode: toUpperText(initialPatient?.postcode ?? ''),
      prescriberGocNumber: '',
      voucherType: '',
      supplements: [],
      chargedAmount: '',
      eligibility: NHS_ELIGIBILITY_FLAGS.reduce(
        (acc, f) => ({ ...acc, [f.key]: Boolean(initialEligibility?.[f.key]) }),
        {} as Record<EligibilityFlagKey, boolean>,
      ),
      isEvidenceSeen: Boolean(initialEligibility?.isEvidenceSeen),
      testDate: initialTestDate ?? new Date().toISOString().slice(0, 10),
      patientDeclared: false,
      contractorSignedOff: false,
      patientSignature: '',
      patientSignatoryName: '',
      optometristSignature: '',
      optometristName: '',
      supplierSignature: '',
      supplierName: '',
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      const now = new Date().toISOString();
      const nhsEligibility: NhsEligibility = {
        isUnder16: values.eligibility.isUnder16,
        isOver60: values.eligibility.isOver60,
        isStudent: values.eligibility.isStudent,
        isBenefits: values.eligibility.isBenefits,
        isGlaucomaDiabetes: values.eligibility.isGlaucomaDiabetes,
        isFamilyHistory: values.eligibility.isFamilyHistory,
        isEvidenceSeen: values.isEvidenceSeen,
      };

      const payload: SubmitClaimPayload = {
        claimType: 'GOS3',
        patientId,
        odsCode,
        patientNhsId,
        patient: {
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: values.dateOfBirth,
          niNumber: values.niNumber || undefined,
          nhsNumber: values.nhsNumber || undefined,
          addressLine1: values.addressLine1 || undefined,
          town: values.town || undefined,
          postcode: values.postcode || undefined,
        },
        // Send the patient's actual powers. Sending empty eyes made the claim
        // state plano in the Prescription block regardless of the refraction.
        prescription: values.testDate
          ? {
              testDate: values.testDate,
              right: initialPrescription?.right ?? {},
              left: initialPrescription?.left ?? {},
              prescriberGocNumber: values.prescriberGocNumber || undefined,
            }
          : undefined,
        voucher: {
          type: values.voucherType as PcseVoucherType,
          supplements: values.supplements,
          chargedAmount: values.chargedAmount
            ? Number(values.chargedAmount)
            : undefined,
        },
        declaration: {
          nhsEligibility,
          patientDeclared: values.patientDeclared,
          contractorSignedOff: values.contractorSignedOff,
          signatures: [
            {
              signatory: 'patient',
              name: values.patientSignatoryName,
              image: values.patientSignature,
              signedAt: now,
            },
            {
              signatory: 'optometrist',
              name: values.optometristName,
              image: values.optometristSignature,
              signedAt: now,
            },
            {
              signatory: 'supplier',
              name: values.supplierName,
              image: values.supplierSignature,
              signedAt: now,
            },
          ],
        },
      };

      try {
        const claim = await submit(payload);
        enqueueSnackbar(
          `Claim ${claim.id.slice(0, 8)} submitted (${claim.status})`,
          {
            variant:
              claim.status === 'rejected' || claim.status === 'error'
                ? 'warning'
                : 'success',
          },
        );
        onSubmitted?.(claim.id);
        helpers.resetForm();
      } catch {
        // error + fieldErrors are surfaced from the hook state below.
      }
    },
  });

  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    getFieldProps,
  } = formik;

  // NHS form: every free-text field is captured in CAPITALS.
  const upperChange = useMemo(
    () =>
      withUppercase(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          handleChange(event),
      ),
    [handleChange],
  );

  // Band values come from the server's rate table — never from a table in the
  // UI, which would go stale the moment the values are renegotiated.
  const {
    typeOptions,
    loading: ratesLoading,
    error: ratesError,
    unconfigured: ratesUnconfigured,
  } = useNhsVoucherRates();

  const hasRx = Boolean(initialPrescription?.found);

  // And so does the valuation: this is the same calculation that later builds
  // the GOS3 claim, so the figure quoted here is the figure actually claimed.
  //
  // The prescription is always sent, so the server derives the band from it.
  // A band the user has picked is sent too and wins — letting them override a
  // derivation they disagree with, while `band.derivedType` still reports what
  // the prescription implied so a mismatch is visible rather than silent.
  const { quote: estimate, loading: estimateLoading } = useVoucherQuote(
    hasRx || values.voucherType
      ? {
          right: initialPrescription?.right,
          left: initialPrescription?.left,
          type: values.voucherType
            ? (values.voucherType as PcseVoucherType)
            : undefined,
          supplements: values.supplements,
          chargedAmount: values.chargedAmount
            ? Number(values.chargedAmount)
            : undefined,
        }
      : null,
  );

  // Adopt the derived band once, as a default the user can still change.
  const derivedType = estimate?.band?.derivedType;
  useEffect(() => {
    if (derivedType && !values.voucherType) {
      setFieldValue('voucherType', derivedType);
    }
  }, [derivedType, values.voucherType, setFieldValue]);

  /** True when the user has picked a band the prescription does not imply. */
  const bandOverridden = Boolean(
    derivedType && values.voucherType && values.voucherType !== derivedType,
  );

  /**
   * The appliance costs less than the voucher is worth, so the NHS pays only
   * what was charged and the balance is lost. Worth calling out: it usually
   * means the amount charged is wrong, not that the practice is giving change.
   */
  const voucherCapped = Boolean(
    estimate && estimate.redeemedValue < estimate.voucherValue,
  );

  // Map any backend/Capita field errors onto helper text by field name.
  const backendFieldError = (name: string): string | undefined =>
    fieldErrors.find(
      (e) => e.field && e.field.toLowerCase().includes(name.toLowerCase()),
    )?.message;

  const helper = (name: keyof FormValues) =>
    (touched[name] && (errors[name] as string)) || backendFieldError(name);

  const toggleSupplement = (key: PcseVoucherSupplement) => {
    const next = values.supplements.includes(key)
      ? values.supplements.filter((s) => s !== key)
      : [...values.supplements, key];
    setFieldValue('supplements', next);
  };

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Stack spacing={3}>
        {error && (
          <Alert severity="error" onClose={() => undefined}>
            {error}
            {fieldErrors.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {fieldErrors.map((e, i) => (
                  <li key={i}>
                    {e.code ? `[${e.code}] ` : ''}
                    {e.field ? `${e.field}: ` : ''}
                    {e.message}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        {/* ── Patient ─────────────────────────────────────────────── */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Patient
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First name"
                  {...getFieldProps('firstName')}
                  onChange={upperChange}
                  error={Boolean(helper('firstName'))}
                  helperText={helper('firstName')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last name"
                  {...getFieldProps('lastName')}
                  onChange={upperChange}
                  error={Boolean(helper('lastName'))}
                  helperText={helper('lastName')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of birth"
                  InputLabelProps={{ shrink: true }}
                  {...getFieldProps('dateOfBirth')}
                  error={Boolean(helper('dateOfBirth'))}
                  helperText={helper('dateOfBirth')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="NHS number"
                  {...getFieldProps('nhsNumber')}
                  onChange={upperChange}
                  error={Boolean(helper('nhsNumber'))}
                  helperText={helper('nhsNumber')}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Address"
                  required
                  {...getFieldProps('addressLine1')}
                  onChange={upperChange}
                  error={Boolean(helper('addressLine1'))}
                  helperText={helper('addressLine1') || 'Required by PCSE'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Town"
                  {...getFieldProps('town')}
                  onChange={upperChange}
                  error={Boolean(helper('town'))}
                  helperText={helper('town')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postcode"
                  required
                  {...getFieldProps('postcode')}
                  onChange={upperChange}
                  error={Boolean(helper('postcode'))}
                  helperText={helper('postcode') || 'Required by PCSE'}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── Voucher ─────────────────────────────────────────────── */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Optical voucher (GOS3)
            </Typography>

            {/* A £0 or missing rate table produces a claim PCSE accepts and
                pays nothing on, while the patient is charged in full — so say
                so before the claim is built, not after. */}
            {(ratesError || ratesUnconfigured) && !ratesLoading && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {ratesError ??
                  'No NHS voucher values are configured, so this claim would be worth £0.00.'}{' '}
                Set them under Settings → NHS voucher rates.
              </Alert>
            )}

            {/* Show the prescription the band came from. Without this the
                chosen band is unexplained, and a wrong refraction (or none at
                all) is invisible until PCSE rejects or underpays the claim. */}
            {hasRx ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Prescription from the patient&apos;s latest refraction — R{' '}
                <strong>{describeEye(initialPrescription!.right)}</strong> · L{' '}
                <strong>{describeEye(initialPrescription!.left)}</strong>
                {estimate?.band?.reason && (
                  <>
                    <br />
                    Voucher band <strong>{estimate.band.derivedType}</strong> (
                    {estimate.band.reason}).
                  </>
                )}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No refraction found for this patient, so the voucher band cannot
                be derived — choose it below. The claim will state plano powers
                in its prescription block.
              </Alert>
            )}

            {bandOverridden && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have selected band <strong>{values.voucherType}</strong>,
                but this prescription gives band <strong>{derivedType}</strong>.
                The selected band is what will be claimed — make sure that is
                intended.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Voucher type"
                  {...getFieldProps('voucherType')}
                  error={Boolean(helper('voucherType'))}
                  helperText={helper('voucherType')}
                >
                  {typeOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount charged"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  {...getFieldProps('chargedAmount')}
                  error={Boolean(helper('chargedAmount'))}
                  helperText={helper('chargedAmount')}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Supplements
                </Typography>
                <Stack direction="row" flexWrap="wrap">
                  {SUPPLEMENT_KEYS.map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={values.supplements.includes(key)}
                          onChange={() => toggleSupplement(key)}
                        />
                      }
                      label={VOUCHER_SUPPLEMENTS[key].label}
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>

            {estimate && !estimateLoading && (
              <Alert
                severity={voucherCapped ? 'warning' : 'info'}
                sx={{ mt: 2 }}
              >
                Claimed from PCSE{' '}
                <strong>£{estimate.redeemedValue.toFixed(2)}</strong>
                {' · '}Patient pays{' '}
                <strong>£{estimate.patientContribution.toFixed(2)}</strong>.
                <br />
                <Typography variant="caption" color="text.secondary">
                  Band {estimate.band.type} is worth £
                  {estimate.baseValue.toFixed(2)}
                  {estimate.supplementValue > 0 && (
                    <>
                      {' '}
                      + £{estimate.supplementValue.toFixed(2)} supplements = £
                      {estimate.voucherValue.toFixed(2)}
                    </>
                  )}
                  .{' '}
                  {voucherCapped ? (
                    <>
                      The appliance costs less than the voucher, so the NHS pays
                      only the £{estimate.redeemedValue.toFixed(2)} charged —
                      the £
                      {(estimate.voucherValue - estimate.redeemedValue).toFixed(
                        2,
                      )}{' '}
                      balance is not paid out.
                    </>
                  ) : (
                    <>
                      The patient pays the £
                      {estimate.patientContribution.toFixed(2)} charged above
                      the voucher.
                    </>
                  )}{' '}
                  Adjudication is still PCSE&apos;s decision.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* ── Eligibility & declaration ────────────────────────────── */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              NHS eligibility & declaration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <Typography variant="subtitle2" gutterBottom>
                  NHS eligibility reason(s)
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {NHS_ELIGIBILITY_FLAGS.map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={
                        <Checkbox
                          name={`eligibility.${f.key}`}
                          checked={values.eligibility[f.key]}
                          onChange={handleChange}
                        />
                      }
                      label={f.label}
                    />
                  ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isEvidenceSeen"
                        checked={values.isEvidenceSeen}
                        onChange={handleChange}
                      />
                    }
                    label="Evidence seen & verified"
                  />
                </Box>
                {typeof errors.eligibility === 'string' && (
                  <Typography variant="caption" color="error" display="block">
                    {errors.eligibility}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="NI number"
                  placeholder="QQ123456C"
                  {...getFieldProps('niNumber')}
                  onChange={upperChange}
                  error={Boolean(helper('niNumber'))}
                  helperText={
                    helper('niNumber') ||
                    (values.eligibility.isBenefits
                      ? 'Required when claiming on a benefits basis'
                      : undefined)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="patientDeclared"
                      checked={values.patientDeclared}
                      onChange={handleChange}
                    />
                  }
                  label="I declare the information given is correct and I am entitled to the help claimed (patient/guardian)."
                />
                {touched.patientDeclared && errors.patientDeclared && (
                  <Typography variant="caption" color="error" display="block">
                    {errors.patientDeclared}
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="contractorSignedOff"
                      checked={values.contractorSignedOff}
                      onChange={handleChange}
                    />
                  }
                  label="Contractor / optometrist sign-off: the claim details are correct."
                />
                {touched.contractorSignedOff && errors.contractorSignedOff && (
                  <Typography variant="caption" color="error" display="block">
                    {errors.contractorSignedOff}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Signed by (patient/guardian)"
                  sx={{ mb: 1 }}
                  {...getFieldProps('patientSignatoryName')}
                  onChange={upperChange}
                  error={Boolean(helper('patientSignatoryName'))}
                  helperText={helper('patientSignatoryName')}
                />
                <SignaturePad
                  label="Patient / guardian signature"
                  value={values.patientSignature}
                  onChange={(v) => setFieldValue('patientSignature', v)}
                  error={
                    touched.patientSignature
                      ? (errors.patientSignature as string)
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Optometrist name"
                  sx={{ mb: 1 }}
                  {...getFieldProps('optometristName')}
                  onChange={upperChange}
                  error={Boolean(helper('optometristName'))}
                  helperText={helper('optometristName')}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Optometrist GOC number"
                  required
                  sx={{ mb: 1 }}
                  {...getFieldProps('prescriberGocNumber')}
                  onChange={upperChange}
                  error={Boolean(helper('prescriberGocNumber'))}
                  helperText={
                    helper('prescriberGocNumber') ||
                    'GOC registration number, required by PCSE'
                  }
                />
                <SignaturePad
                  label="Optometrist signature"
                  value={values.optometristSignature}
                  onChange={(v) => setFieldValue('optometristSignature', v)}
                  error={
                    touched.optometristSignature
                      ? (errors.optometristSignature as string)
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Supplier / dispenser name"
                  required
                  sx={{ mb: 1 }}
                  {...getFieldProps('supplierName')}
                  onChange={upperChange}
                  error={Boolean(helper('supplierName'))}
                  helperText={helper('supplierName')}
                />
                <SignaturePad
                  label="Supplier / dispenser signature"
                  value={values.supplierSignature}
                  onChange={(v) => setFieldValue('supplierSignature', v)}
                  error={
                    touched.supplierSignature
                      ? (errors.supplierSignature as string)
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            loading={loading}
            disabled={loading}
          >
            Submit claim to PCSE
          </LoadingButton>
        </Box>
      </Stack>
    </form>
  );
}
